import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import Drawer from '@material-ui/core/Drawer';
import Typography from '@material-ui/core/Typography';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import CookieCheckIcon from 'mdi-react/CookieCheckIcon';
import { changeCookieConsent, CookieConsentType, loadCookieConsent, saveCookieConsent } from '../lib/cookieConsent';
import { Button } from './Button';
import { Color, layoutStyles, mediaQuery } from './Theme';

const Container = styled.div`
  ${layoutStyles}
  padding: 4rem 0;

  > .MuiTypography-h5 {
    display: flex;
    align-items: center;
    margin-bottom: 1rem;

    svg {
      margin-right: 0.6rem;
    }
  }

  > .MuiTypography-body1 {
    margin-bottom: 1rem;
    max-width: 800px;
    font-size: 1.1rem;
    line-height: 1.6rem;
    opacity: 0.75;
  }
`;

const Options = styled.div`
  display: flex;
  padding-top: 0.5rem;

  ${mediaQuery.small(css`
    display: block;
  `)}
`;

const Actions = styled.div`
  display: flex;
  padding-top: 1rem;
  justify-content: flex-end;

  > button:not(:last-child) {
    margin-right: 1rem;
  }

  ${mediaQuery.small(css`
    flex-direction: column-reverse;

    > button:not(:last-child) {
      margin-right: 0;
      margin-bottom: 1rem;
    }
  `)}
`;

/**
 * Shows a cookie consent banner upon first visit
 * NOTE: This should only ever be included with client side code, NEVER with SSR!
 */
const CookieConsent: React.FC = ({}) => {
  const [consentState, _setConsentState] = useState<false | null | string>(false);
  const [showDetails, setShowDetails] = useState(false);
  const [checkboxState, setCheckboxState] = useState({ statistics: true, marketing: true });

  useEffect(() => {
    setTimeout(() => _setConsentState(loadCookieConsent() || null), 500);
  }, []);

  // Set the consent state and close banner
  const setConsentState = useCallback((consent: CookieConsentType[]) => {
    // Use 0 as "functional"
    const paddedConsent = [0, ...consent];
    saveCookieConsent(paddedConsent);
    changeCookieConsent(paddedConsent);
    _setConsentState(paddedConsent.join(','));
  }, []);

  const toggleCheckbox = useCallback((checkbox: keyof typeof checkboxState, checked: boolean) => {
    setCheckboxState((state) => ({ ...state, [checkbox]: checked }));
  }, []);

  const acceptAll = useCallback(() => setConsentState([CookieConsentType.STATISTICS, CookieConsentType.MARKETING]), []);
  const onlyNecessary = useCallback(() => setConsentState([]), []);
  const allowSelected = useCallback(() => {
    const consentState = [];
    if (checkboxState.statistics) consentState.push(CookieConsentType.STATISTICS);
    if (checkboxState.marketing) consentState.push(CookieConsentType.MARKETING);
    setConsentState(consentState);
  }, [checkboxState]);

  return (
    <Drawer open={consentState === null} hideBackdrop transitionDuration={{ enter: 700 }} variant="persistent" anchor="bottom">
      <Container>
        <Typography variant="h5">
          <CookieCheckIcon size={32} color={Color.secondary} />
          <FormattedMessage id="cookieConsent.title" defaultMessage="Constle käyttää evästeitä" />
        </Typography>

        <Typography>
          <FormattedMessage
            id="cookieConsent.title"
            defaultMessage="Constle käyttää evästeitä paremman käyttäjäkokemuksen saavuttamiseksi. Evästeiden käyttö rajoittuu statistiikan ja virheilmoitusten käsittelyyn sekä keräämiseen."
          />
        </Typography>

        <Options style={showDetails ? {} : { display: 'none' }}>
          <FormControlLabel
            label={<FormattedMessage id="cookieConsent.cookieType.functional" defaultMessage="Toiminnalliset" />}
            control={<Checkbox color="primary" checked disabled />}
          />
          <FormControlLabel
            label={<FormattedMessage id="cookieConsent.cookieType.statistics" defaultMessage="Statistiikka" />}
            control={
              <Checkbox
                color="primary"
                checked={checkboxState.statistics}
                onChange={(_event, checked) => toggleCheckbox('statistics', checked)}
              />
            }
          />
          <FormControlLabel
            label={<FormattedMessage id="cookieConsent.cookieType.marketing" defaultMessage="Markkinointi" />}
            control={
              <Checkbox
                color="primary"
                checked={checkboxState.marketing}
                onChange={(_event, checked) => toggleCheckbox('marketing', checked)}
              />
            }
          />
        </Options>

        <Actions>
          {showDetails && (
            <>
              <Button variant="text" color="default" onClick={onlyNecessary}>
                <FormattedMessage id="cookieConsent.onlyNecessaryButton" defaultMessage="Vain pakolliset" />
              </Button>
              <Button onClick={allowSelected}>
                <FormattedMessage id="cookieConsent.allowSelectedButton" defaultMessage="Hyväksy valitut" />
              </Button>
            </>
          )}

          {!showDetails && (
            <>
              <Button variant="text" color="default" onClick={() => setShowDetails(true)}>
                <FormattedMessage id="cookieConsent.manageChoicesButton" defaultMessage="Muokkaa valintoja" />
              </Button>
              <Button variant="contained" color="primary" onClick={acceptAll}>
                <FormattedMessage id="cookieConsent.acceptButton" defaultMessage="Hyväksy" />
              </Button>
            </>
          )}
        </Actions>
      </Container>
    </Drawer>
  );
};

/** Has to be a default export to use Next.js dynamic */
export default CookieConsent;
